import { csx, Flex, Text, Tooltip } from '@vtex/admin-ui'
import { FormattedMessage, useIntl } from 'react-intl'
import { ZIP_CODE_RANGE_OPERATOR, ZipCodeRange } from 'typings'
import { Tag } from '../../../../Tag'
import { Rule, RuleContent, RuleDescription } from '../../../RuleStack'
import { messages } from '../messages'

const MAX_ZIPCODE_RANGES_ON_PREVIEW = 3

type OverflowTagProps = {
  zipCodeRanges: ZipCodeRange[]
}

function OverflowTag(props: OverflowTagProps) {
  const { zipCodeRanges } = props
  const { $t, formatList, formatNumber } = useIntl()

  const tooltipContent = formatList(
    zipCodeRanges.slice(MAX_ZIPCODE_RANGES_ON_PREVIEW).map(({ from, to }) =>
      $t(messages.view.range, {
        from,
        to,
        tag: (content) => content,
      })
    ),
    { type: 'disjunction' }
  ) as string

  return (
    <Tooltip text={tooltipContent}>
      <Tag
        className={csx({
          marginLeft: '$space-2',
        })}
      >
        {formatNumber(zipCodeRanges.length - MAX_ZIPCODE_RANGES_ON_PREVIEW, {
          signDisplay: 'always',
        })}
      </Tag>
    </Tooltip>
  )
}

type ZipCodeRangesRuleProps = {
  zipCodeRanges: ZipCodeRange[]
  operator: ZIP_CODE_RANGE_OPERATOR
}

export function ZipCodeRangesRule(props: ZipCodeRangesRuleProps) {
  const { $t } = useIntl()
  const { zipCodeRanges, operator } = props

  if (!zipCodeRanges.length) return

  const listOverflow = zipCodeRanges.length > MAX_ZIPCODE_RANGES_ON_PREVIEW

  return (
    <Rule>
      <RuleDescription>
        {$t(messages.view.zipCodeRange, {
          b: (content: React.ReactNode) => (
            <Text variant="body">{content}</Text>
          ),
          inclusive: operator === ZIP_CODE_RANGE_OPERATOR.INCLUDE,
        })}
      </RuleDescription>

      <RuleContent>
        <Flex direction="column" className={csx({ rowGap: '$space-2' })}>
          {zipCodeRanges
            .slice(0, MAX_ZIPCODE_RANGES_ON_PREVIEW)
            .map(({ from, to }, index) => {
              const isLastVisibleItem =
                MAX_ZIPCODE_RANGES_ON_PREVIEW === index + 1

              return (
                <div key={index}>
                  <FormattedMessage
                    {...messages.view.range}
                    values={{
                      from,
                      to,
                      tag: (content) => <Tag>{content}</Tag>,
                    }}
                  />

                  {listOverflow && isLastVisibleItem && (
                    <OverflowTag zipCodeRanges={zipCodeRanges} />
                  )}
                </div>
              )
            })}
        </Flex>
      </RuleContent>
    </Rule>
  )
}
